import { dayjs } from "@thalamos/common";
import { MhaStatus } from "../types/patientState/PatientState.js";
import { S132Outcome } from "../types/s132/S132.js";
import { isInSection136Bucket } from "./dualStatus.js";
/**
 * Which information duty each status owes. Exhaustive by construction, so a new
 * status fails the build here until someone decides whether it owes one.
 *
 * Every recorded Section 136 is treated as owing s132, since the Code of
 * Practice expects hospital managers to comply with s132 where a hospital is the
 * place of safety.
 * TODO(s132): confirm whether a 136 in a non-hospital place of safety should owe it.
 */
export const S132_DUTY_FOR = {
    [MhaStatus.Section2]: "s132",
    [MhaStatus.Section3]: "s132",
    [MhaStatus.Section4]: "s132",
    [MhaStatus.Section5_2]: "s132",
    [MhaStatus.Section5_4]: "s132",
    [MhaStatus.Section136]: "s132",
    [MhaStatus.Section17A]: "s132A",
    [MhaStatus.Unknown]: null,
    [MhaStatus.Incomplete]: null,
    [MhaStatus.Expired]: null,
    [MhaStatus.NotDetained]: null,
};
export const s132SectionsOwingDuty = Object.keys(S132_DUTY_FOR).filter((status) => S132_DUTY_FOR[status] !== null);
// A renewal or a new provision carries a new startDateTime and so starts a new
// period, which is what makes the duty fall due again. Rows without one fall
// back to their own effective time.
const periodStartOf = (row) => dayjs(row.state.mhaStatus.startDateTime ?? row.effectiveDateTime).toISOString();
const periodKeyOf = (row) => S132_DUTY_FOR[row.state.mhaStatus.status]
    ? `${row.state.mhaStatus.status}|${periodStartOf(row)}`
    : null;
// The row that ends a period says when in its own terms. Its effective time is a
// last resort: it can predate the change it records (see generateNewPatientState
// for manual events).
const periodEndFrom = (row) => row.state.mhaStatus.startDateTime ??
    row.state.episodeOfCare?.dischargeDateTime ??
    row.effectiveDateTime;
function periodsInBucket(rows) {
    const periods = [];
    let open = null;
    for (const row of rows) {
        const key = periodKeyOf(row);
        if (open && key !== open.key) {
            const end = dayjs.max(dayjs(periodEndFrom(row)), dayjs(open.start));
            periods.push({ ...open, end: end.toISOString() });
            open = null;
        }
        const duty = S132_DUTY_FOR[row.state.mhaStatus.status];
        if (key && duty && !open) {
            open = {
                key,
                section: row.state.mhaStatus.status,
                duty,
                start: periodStartOf(row),
            };
        }
    }
    if (open)
        periods.push(open);
    return periods;
}
/**
 * Each patient in a merge tree has a history of their own, so each is walked
 * alone and one patient's change of section cannot end another's period. A
 * period left open when a patient's own rows stop, and not current, was
 * overtaken when the patient was merged, so it ends at the merge. Where that is
 * not known, or the period began after it, it ends at the first row another
 * patient recorded later that does not hold it.
 */
function periodsAcrossMergeTree(rows, currentKeys, mergedAt) {
    const byPatient = new Map();
    for (const row of rows) {
        byPatient.set(row.patientId, [
            ...(byPatient.get(row.patientId) ?? []),
            row,
        ]);
    }
    return [...byPatient.values()].flatMap((own) => {
        const periods = periodsInBucket(own);
        const last = periods.at(-1);
        const lastRow = own.at(-1);
        if (last === undefined ||
            lastRow === undefined ||
            last.end !== undefined ||
            currentKeys.has(last.key)) {
            return periods;
        }
        const merged = mergedAt?.[lastRow.patientId];
        const ender = rows
            .slice(rows.indexOf(lastRow) + 1)
            .find((row) => periodKeyOf(row) !== last.key);
        const endedAt = merged !== undefined && dayjs(merged).isAfter(last.start)
            ? merged
            : ender && periodEndFrom(ender);
        if (endedAt === undefined)
            return periods;
        const end = dayjs.max(dayjs(endedAt), dayjs(last.start));
        return [...periods.slice(0, -1), { ...last, end: end.toISOString() }];
    });
}
/**
 * A key names one period: a section and its start, so spans that share a key
 * are one period, and one a surviving latest row still holds stays open.
 */
function onePeriodPerKey(periods, currentKeys) {
    const byKey = new Map();
    for (const period of periods) {
        const earlier = byKey.get(period.key);
        const { key, section, duty, start } = earlier ?? period;
        const earlierEnd = earlier ? earlier.end : period.end;
        byKey.set(key, currentKeys.has(key) ||
            earlierEnd === undefined ||
            period.end === undefined
            ? { key, section, duty, start }
            : {
                key,
                section,
                duty,
                start,
                end: dayjs.max(dayjs(earlierEnd), dayjs(period.end)).toISOString(),
            });
    }
    return [...byKey.values()];
}
const isWithin = (attempt, period) => {
    const at = dayjs(attempt.occurredAt).valueOf();
    return (attempt.section === period.section &&
        at >= dayjs(period.start).valueOf() &&
        (!period.end || at < dayjs(period.end).valueOf()));
};
/** The latest version of each entry, whether or not it has been withdrawn. */
export const latestS132Attempts = (attempts) => {
    const superseded = new Set(attempts.map((a) => a.supersedesId).filter(Boolean));
    return attempts.filter((a) => !superseded.has(a.id));
};
/** Attempts that count: the latest version of each entry not withdrawn. */
export const currentS132Attempts = (attempts) => latestS132Attempts(attempts).filter((a) => !a.withdrawal);
/** Every version of an entry, from its latest back to the original. */
export function s132AttemptChain(latest, attempts) {
    const byId = new Map(attempts.map((a) => [a.id, a]));
    const chain = [];
    for (let at = latest; at; at = at.supersedesId ? byId.get(at.supersedesId) : undefined) {
        chain.push(at);
    }
    return chain;
}
/**
 * The duty is never stored: it is derived from the patient's state history and
 * the attempts recorded against them, so a state entered late, corrected or
 * deleted changes the answer without anything to reconcile.
 *
 * `history` must be in the order the rows were recorded, not by effective time:
 * a patient's placeholder state is recorded before their first section but
 * carries a later effective time, and must not end that section. `latest`
 * decides which periods are current; it is folded into `history` so a caller
 * may pass the latest rows alone to evaluate only what is current.
 */
export function deriveS132Record(history, latest, attempts, options = {}) {
    const byId = new Map(history.map((row) => [row.id, row]));
    for (const row of latest)
        byId.set(row.id, row);
    const rows = [...byId.values()];
    const latestKeys = new Set(latest.map(periodKeyOf).filter((key) => key !== null));
    const open = onePeriodPerKey([
        // A Section 136 can run alongside another detention, so each is
        // sequenced on its own rather than ending the other.
        ...periodsAcrossMergeTree(rows.filter(isInSection136Bucket), latestKeys, options.mergedAt),
        ...periodsAcrossMergeTree(rows.filter((row) => !isInSection136Bucket(row)), latestKeys, options.mergedAt),
    ], latestKeys).filter(
    // A period ended at its own start held no time, so no attempt can fall in
    // it: a Section 136 whose clock is corrected earlier leaves one behind.
    (period) => period.end === undefined || !dayjs(period.end).isSame(period.start));
    const effective = currentS132Attempts(attempts);
    const matched = new Set();
    const countFrom = options.countFrom
        ? dayjs(options.countFrom).valueOf()
        : undefined;
    const periods = open.map((period) => {
        const inPeriod = effective.filter((a) => isWithin(a, period));
        inPeriod.forEach((a) => matched.add(a.id));
        const understood = inPeriod
            .filter((a) => a.outcome === S132Outcome.Understood)
            .map((a) => a.occurredAt)
            .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
        const status = countFrom !== undefined && dayjs(period.start).valueOf() < countFrom
            ? "notTracked"
            : understood.length > 0
                ? "understood"
                : inPeriod.length > 0
                    ? "notYetUnderstood"
                    : "notRecorded";
        return {
            section: period.section,
            duty: period.duty,
            start: period.start,
            end: period.end,
            current: !period.end && latestKeys.has(period.key),
            status,
            attemptIds: inPeriod.map((a) => a.id),
            firstUnderstoodAt: understood[0],
        };
    });
    periods.sort((a, b) => dayjs(b.start).valueOf() - dayjs(a.start).valueOf());
    return {
        periods,
        attempts,
        unmatchedAttemptIds: effective
            .filter((a) => !matched.has(a.id))
            .map((a) => a.id),
    };
}
/** The status of each current period, e.g. for a banner or a dashboard row. */
export function currentS132Statuses(latest, attempts, options = {}) {
    return deriveS132Record(latest, latest, attempts, options)
        .periods.filter((p) => p.current)
        .map((p) => ({
        section: p.section,
        duty: p.duty,
        status: p.status,
        attemptCount: p.attemptIds.length,
    }));
}
export const s132StatusIsOutstanding = (status) => status === "notRecorded" || status === "notYetUnderstood";
//# sourceMappingURL=s132.js.map